import React, { ChangeEvent, FC, useMemo } from "react";
import classes from "./ItemIconEditor.module.scss";
import CustomButton from "../CustomButton/CustomButton";
import { ItemIconSettings } from "../../../types/Admin/ItemIconTypes";
import {
  defaultItemIconSettings,
  getMediaBackgroundSize,
  normalizeMediaUrl,
} from "../../../utils/mediaBackground";

type ItemIconEditorProps = {
  value: ItemIconSettings;
  onChange: (value: ItemIconSettings) => void;
  onUpload: (file: File) => Promise<string | void>;
  onDelete?: () => void | Promise<void>;
  isUploading?: boolean;
  idPrefix: string;
};

const parseNumberInput = (raw: string, fallback: number) => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const ItemIconEditor: FC<ItemIconEditorProps> = ({
  value,
  onChange,
  onUpload,
  onDelete,
  isUploading = false,
  idPrefix,
}) => {
  const fitMode = value.iconFitMode ?? defaultItemIconSettings.iconFitMode;
  const scale = value.iconScale ?? defaultItemIconSettings.iconScale;
  const positionX =
    value.iconPositionX ?? defaultItemIconSettings.iconPositionX;
  const positionY =
    value.iconPositionY ?? defaultItemIconSettings.iconPositionY;

  const previewUrl = useMemo(
    () => (value.iconUrl ? normalizeMediaUrl(value.iconUrl) : ""),
    [value.iconUrl],
  );

  const updateField = <K extends keyof ItemIconSettings>(
    key: K,
    fieldValue: ItemIconSettings[K],
  ) => {
    onChange({ ...value, [key]: fieldValue });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const uploadedUrl = await onUpload(file);
    if (uploadedUrl) {
      updateField("iconUrl", uploadedUrl);
    }
  };

  const handleRemove = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (onDelete) {
      await onDelete();
    }
    onChange({
      ...value,
      iconUrl: null,
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.uploadRow}>
        <label htmlFor={`${idPrefix}-icon-upload`}>
          Иконка (jpg, jpeg, png, webp, gif, svg)
        </label>
        <div className={classes.uploadInputRow}>
          {!previewUrl && (
            <input
              id={`${idPrefix}-icon-upload`}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          )}
          {previewUrl && (
            <CustomButton
              content="Удалить"
              styleType="default"
              onClick={handleRemove}
            />
          )}
        </div>
      </div>
      {isUploading && <span className={classes.loading}>Загрузка...</span>}
      <div className={classes.controls}>
        <label>
          Режим отображения
          <select
            className={classes.displayModeSelect}
            value={fitMode}
            onChange={(event) => updateField("iconFitMode", event.target.value)}
          >
            <option value="scale">Свой размер</option>
            <option value="contain">Вписать в область</option>
            <option value="cover">Заполнить область</option>
            <option value="stretch">Растянуть (по ширине и высоте)</option>
            <option value="width">Растянуть по ширине</option>
            <option value="height">Растянуть по высоте</option>
            <option value="none">Оригинальный размер</option>
          </select>
        </label>
        <label className={classes.rangeField}>
          Масштаб
          <div className={classes.rangeRow}>
            <input
              type="range"
              min={50}
              max={500}
              step={1}
              value={scale}
              onChange={(event) =>
                updateField("iconScale", Number(event.target.value))
              }
            />
            <input
              type="number"
              min={50}
              max={500}
              value={scale}
              onChange={(event) =>
                updateField(
                  "iconScale",
                  parseNumberInput(event.target.value, scale),
                )
              }
            />
            <span>%</span>
          </div>
        </label>
        <label className={classes.rangeField}>
          Позиция по X
          <div className={classes.rangeRow}>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={positionX}
              onChange={(event) =>
                updateField("iconPositionX", Number(event.target.value))
              }
            />
            <input
              type="number"
              min={0}
              max={100}
              value={positionX}
              onChange={(event) =>
                updateField(
                  "iconPositionX",
                  parseNumberInput(event.target.value, positionX),
                )
              }
            />
            <span>%</span>
          </div>
        </label>
        <label className={classes.rangeField}>
          Позиция по Y
          <div className={classes.rangeRow}>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={positionY}
              onChange={(event) =>
                updateField("iconPositionY", Number(event.target.value))
              }
            />
            <input
              type="number"
              min={0}
              max={100}
              value={positionY}
              onChange={(event) =>
                updateField(
                  "iconPositionY",
                  parseNumberInput(event.target.value, positionY),
                )
              }
            />
            <span>%</span>
          </div>
        </label>
      </div>
      {previewUrl && (
        <div className={classes.previewCard}>
          <div className={classes.preview}>
            <div
              className={classes.previewImage}
              style={{
                backgroundImage: `url(${previewUrl})`,
                backgroundSize: getMediaBackgroundSize(fitMode, scale),
                backgroundPosition: `${positionX}% ${positionY}%`,
              }}
              aria-label="Предпросмотр иконки"
            />
          </div>
        </div>
      )}
    </div>
  );
};
